import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { FeaturesProvider, FeatureGate } from "./lib/features";
import { ThemeProvider } from "./lib/theme";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import DashboardLayout from "./components/DashboardLayout";
import DashboardHome from "./pages/DashboardHome";
import OptimizePage from "./pages/OptimizePage";
import HistoryPage from "./pages/HistoryPage";
import SettingsPage from "./pages/SettingsPage";
import PrivacyCenterPage from "./pages/PrivacyCenterPage";
import UserAnalyticsPage from "./pages/UserAnalyticsPage";
import ResumeManagerPage from "./pages/ResumeManagerPage";
import JobTrackerPage from "./pages/JobTrackerPage";
import ApplicationDetailPage from "./pages/ApplicationDetailPage";
import CoverLetterPage from "./pages/CoverLetterPage";
import WriteEmailPage from "./pages/WriteEmailPage";
import ATSScorePage from "./pages/ATSScorePage";
import PricingPage from "./pages/PricingPage";
import AdminLayout from "./components/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminRevenue from "./pages/admin/AdminRevenue";
import AdminPromos from "./pages/admin/AdminPromos";
import AdminAnalyticsPage from "./pages/admin/AdminAnalyticsPage";
import AdminCostDashboard from "./pages/admin/AdminCostDashboard";
import AdminFeatures from "./pages/admin/AdminFeatures";
import ManageSubscriptions from "./pages/admin/ManageSubscriptions";
import AdminAnnouncements from "./pages/admin/AdminAnnouncements";
import ProfilePage from "./pages/ProfilePage";
import PublicProfilePage from "./pages/PublicProfilePage";
import SquadsPage from "./pages/SquadsPage";
import SquadDetailPage from "./pages/SquadDetailPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import AgenciesPage from "./pages/AgenciesPage";
import AgencyDashboardPage from "./pages/AgencyDashboardPage";
import AgencyTeamPage from "./pages/AgencyTeamPage";
import NotificationsPage from "./pages/NotificationsPage";
import ExtensionSidebar from "./pages/ExtensionSidebar";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="auth-loading" data-testid="auth-loading">
        <Loader2 size={32} className="spin" />
        <span>Loading...</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RedirectIfAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <FeaturesProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<RedirectIfAuth><LoginPage /></RedirectIfAuth>} />
          <Route path="/signup" element={<RedirectIfAuth><SignupPage /></RedirectIfAuth>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/app" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            <Route index element={<FeatureGate lockKey="dashboard_locked"><DashboardHome /></FeatureGate>} />
            <Route path="optimize" element={<FeatureGate lockKey="optimize_locked"><OptimizePage /></FeatureGate>} />
            <Route path="match-score" element={<FeatureGate lockKey="match_score_locked"><ATSScorePage /></FeatureGate>} />
            <Route path="cover-letter" element={<FeatureGate lockKey="cover_letter_locked"><CoverLetterPage /></FeatureGate>} />
            <Route path="write-email" element={<FeatureGate lockKey="application_email_locked"><WriteEmailPage /></FeatureGate>} />
            <Route path="resumes" element={<FeatureGate lockKey="resumes_locked"><ResumeManagerPage /></FeatureGate>} />
            <Route path="history" element={<FeatureGate lockKey="history_locked"><HistoryPage /></FeatureGate>} />
            <Route path="tracker" element={<FeatureGate lockKey="job_tracker_locked"><JobTrackerPage /></FeatureGate>} />
            <Route path="tracker/:appId" element={<FeatureGate lockKey="job_tracker_locked"><ApplicationDetailPage /></FeatureGate>} />
            <Route path="pricing" element={<FeatureGate flag="pricing_enabled" lockKey="pricing_locked"><PricingPage /></FeatureGate>} />
            <Route path="analytics" element={<FeatureGate lockKey="analytics_locked"><UserAnalyticsPage /></FeatureGate>} />
            <Route path="profile" element={<FeatureGate lockKey="profile_locked"><ProfilePage /></FeatureGate>} />
            <Route path="u/:username" element={<PublicProfilePage />} />
            <Route path="squads" element={<FeatureGate flag="squads_enabled" lockKey="squads_locked"><SquadsPage /></FeatureGate>} />
            <Route path="squads/:squadId" element={<FeatureGate flag="squads_enabled" lockKey="squads_locked"><SquadDetailPage /></FeatureGate>} />
            <Route path="agencies" element={<FeatureGate flag="agencies_enabled" lockKey="agencies_locked"><AgenciesPage /></FeatureGate>} />
            <Route path="agencies/:agencyId" element={<FeatureGate flag="agencies_enabled" lockKey="agencies_locked"><AgencyDashboardPage /></FeatureGate>} />
            <Route path="agencies/:agencyId/teams/:teamId" element={<FeatureGate flag="agencies_enabled" lockKey="agencies_locked"><AgencyTeamPage /></FeatureGate>} />
            <Route path="leaderboard" element={<FeatureGate flag="leaderboard_visible" lockKey="leaderboard_locked"><LeaderboardPage /></FeatureGate>} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="settings" element={<FeatureGate lockKey="settings_locked"><SettingsPage /></FeatureGate>} />
            <Route path="privacy" element={<FeatureGate lockKey="privacy_locked"><PrivacyCenterPage /></FeatureGate>} />
          </Route>
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="revenue" element={<AdminRevenue />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
            <Route path="costs" element={<AdminCostDashboard />} />
            <Route path="promos" element={<AdminPromos />} />
            <Route path="features" element={<AdminFeatures />} />
            <Route path="subscriptions" element={<ManageSubscriptions />} />
            <Route path="announcements" element={<AdminAnnouncements />} />
          </Route>
          <Route path="/extension" element={<ExtensionSidebar />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </FeaturesProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
